/**
 * Global Search Utilities
 * Helper functions for search operations
 */

import { matchesSearchTerms, matchesExactSearch } from '@/lib/searchUtils';
import { matchesPhoneticFuzzyTerms } from './fuzzySearch';

/**
 * Get the server-side search pattern
 * For fuzzy mode: use only first 2 characters to allow phonetic matching client-side
 * This enables finding "Hirschhofer" when searching "hirsh" (phonetic: hirʃ)
 * For normal mode: use full term
 */
export const getServerPattern = (term: string, fuzzyMatch: boolean): string => {
  const firstTerm = getFirstSearchTerm(term).toLowerCase();
  if (!firstTerm) return '%%';
  
  // In fuzzy mode, use only first 2-3 chars for broader server-side results
  // Client-side fuzzy matching will filter precisely
  if (fuzzyMatch && firstTerm.length >= 2) {
    return `%${firstTerm.substring(0, 2)}%`;
  }
  
  return `%${firstTerm}%`;
};

/**
 * Get the limit multiplier based on search mode
 * Fuzzy mode needs more results from server for client-side filtering
 */
export const getServerLimit = (baseLimit: number, fuzzyMatch: boolean): number => {
  return fuzzyMatch ? baseLimit * 10 : baseLimit * 3;
};

/**
 * Get the appropriate match function based on search settings
 * Priority: exactMatch > fuzzyMatch > default (multi-term)
 */
export const getMatchFunction = (exactMatch: boolean, fuzzyMatch: boolean = false) => {
  if (exactMatch) {
    return matchesExactSearch;
  }
  if (fuzzyMatch) {
    return (searchTerm: string, ...targets: (string | undefined | null)[]) => {
      const terms = searchTerm.trim().split(/\s+/).filter(t => t.length > 0);
      // Use phonetic fuzzy matching for German name tolerance
      return matchesPhoneticFuzzyTerms(terms, ...targets);
    };
  }
  return matchesSearchTerms;
};

// Extract first term for DB query, keep full term for client filtering
export const getFirstSearchTerm = (term: string): string => {
  const terms = term.trim().split(/\s+/).filter(t => t.length > 0);
  return terms[0] || '';
};

/**
 * Normalize text for relevance scoring
 */
const normalizeText = (text: string): string => {
  return text
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/[\s\-_.,;:/\\|()[\]{}<>!?'"`~@#$%^&*+=]+/g, '');
};

/**
 * Calculate relevance score for a search result
 * Higher score = more relevant
 * 
 * Scoring logic:
 * - Exact title match: 100 points
 * - Title starts with search term: 75 points
 * - Title contains search term: 50 points
 * - Subtitle contains search term: 25 points
 * - Recency boost: up to 10 points (decreases over 30 days)
 */
export const calculateRelevanceScore = (
  searchTerm: string,
  title: string,
  subtitle?: string,
  updatedAt?: string
): number => {
  const normalizedSearch = normalizeText(searchTerm);
  const normalizedTitle = normalizeText(title);
  const normalizedSubtitle = subtitle ? normalizeText(subtitle) : '';
  
  let score = 0;
  
  // Exact match in title = highest score
  if (normalizedTitle === normalizedSearch) {
    score += 100;
  }
  // Title starts with search term
  else if (normalizedTitle.startsWith(normalizedSearch)) {
    score += 75;
  }
  // Title contains search term
  else if (normalizedTitle.includes(normalizedSearch)) {
    score += 50;
  }
  
  // Multi-term matching bonus: check each search term individually
  const searchTerms = searchTerm.trim().toLowerCase().split(/\s+/).filter(t => t.length > 0);
  const matchedTerms = searchTerms.filter(term => 
    normalizedTitle.includes(normalizeText(term)) || 
    normalizedSubtitle.includes(normalizeText(term))
  );
  
  // Bonus for matching multiple terms
  if (matchedTerms.length > 1) {
    score += matchedTerms.length * 5;
  }
  
  // Subtitle match (less weight)
  if (normalizedSubtitle && normalizedSubtitle.includes(normalizedSearch)) {
    score += 25;
  }
  
  // Recency boost: prefer recently updated items
  if (updatedAt) {
    const daysSinceUpdate = (Date.now() - new Date(updatedAt).getTime()) / (1000 * 60 * 60 * 24);
    score += Math.max(0, 10 - daysSinceUpdate / 30); // Max 10 points, decreases over 30 days
  }
  
  return score;
};

/**
 * Sort search results by relevance score (descending)
 */
export const sortByRelevance = <T extends { relevanceScore?: number }>(results: T[]): T[] => {
  return [...results].sort((a, b) => (b.relevanceScore || 0) - (a.relevanceScore || 0));
};
